import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import PauseMonitoringBanner from './PauseMonitoringBanner';
import PauseMonitoringModal from './PauseMonitoringModal';
import PauseMonitoringCancelModal from './PauseMonitoringCancelModal';
import PauseMonitoringContextProvider from './PauseMonitoringContext';

interface IProps {
  children: ReactNode;
}

const Patient = ({ children }: IProps): ReactElement => {
  const [showPauseMonitoringPopup, setShowPauseMonitoringPopup] = useState(false);
  const [showResumeMonitoringPopup, setShowResumeMonitoringPopup] = useState(false);

  const patientInfo = useStoreState(state => state.patient.info);
  const currentMonitoringPause = patientInfo?.currentMonitoringPause;

  const { onGetPatient } = useStoreActions(actions => actions.patient);
  const { showError } = useStoreActions(actions => actions.snackbar);

  const onMount = async () => {
    if (!patientInfo?.id) return;

    try {
      await onGetPatient(patientInfo.id);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    onMount();
  }, [patientInfo?.id]);

  const handleRequestResumeMonitoring = () => {
    if (!currentMonitoringPause) return;
    setShowResumeMonitoringPopup(true);
  };

  if (!patientInfo) {
    return <>{children}</>;
  }

  return (
    <PauseMonitoringContextProvider onRequestResumeMonitoring={handleRequestResumeMonitoring}>
      {currentMonitoringPause && (
        <PauseMonitoringBanner
          pause={currentMonitoringPause}
          timezone={patientInfo.timezone}
          onEdit={() => setShowPauseMonitoringPopup(true)}
        />
      )}
      {children}
      {showPauseMonitoringPopup && currentMonitoringPause && (
        <PauseMonitoringModal onClose={() => setShowPauseMonitoringPopup(false)} />
      )}
      {showResumeMonitoringPopup && (
        <PauseMonitoringCancelModal onClose={() => setShowResumeMonitoringPopup(false)} />
      )}
    </PauseMonitoringContextProvider>
  );
};

export default Patient;
